import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useSubscription } from '@apollo/client'
import { useAuth } from '@/components/providers/auth-provider'
import { USER_EVENTS_SUBSCRIPTION } from '@/lib/graphql/voice'
import { RADAR_SWEEP_STATUS } from '@/lib/graphql/radar-sweep'

/**
 * Whether the radar is sweeping, for the whole app, so the menu can show
 * Queue working the way it shows Create writing. A sweep runs on the server
 * whether the user asked for it on the Queue page or the schedule started
 * it, and the Queue page is often closed by then.
 *
 * The server is the source of truth: seeded from the account's status, kept
 * live by the sweep's start and finish events, and checked again every
 * minute while a sweep shows, in case a finish event was lost. Every event
 * and status carries the run's start (lastRunAt), so a status read that
 * arrives after the finish event of the same run cannot switch it back on.
 * The Queue page marks a sweep it asked for right away, and hands over the
 * status its own poll reads.
 */

export interface RadarSweepStatus {
  discoveryStatus?: string | null
  lastRunAt?: string | null
}

interface RadarActivityValue {
  sweeping: boolean
  /** The Queue page asked for a sweep: show it now, the server confirms it shortly. */
  noteSweepStarted: () => void
  /** The Queue page's request returned: ask the server whether the sweep is still going. */
  noteSweepSettled: () => void
  /** A status the Queue page read with its own poll. */
  noteStatus: (status: RadarSweepStatus | null | undefined) => void
}

const RadarActivityContext = createContext<RadarActivityValue | null>(null)

/** A run has a 20-minute ceiling on the server; past this, RUNNING means it died without saying so. */
const STALE_MS = 30 * 60 * 1000
/** A sweep marked here is checked with the server after this, and dropped after the grace if the server has no record of it. */
const LOCAL_CHECK_MS = 2500
const LOCAL_GRACE_MS = 8000
/** While a sweep shows, the server is asked again this often. */
const CHECK_WHILE_SWEEPING_MS = 60_000

function time(value: string | null | undefined): number | null {
  if (!value) return null
  const at = new Date(value).getTime()
  return Number.isFinite(at) ? at : null
}

export function RadarActivityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const enabled = Boolean(user) && !user?.isGuest
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [localMark, setLocalMark] = useState<number | null>(null)
  // The start of the newest run the server said has finished.
  const finishedRun = useRef(0)

  const { data, refetch } = useQuery(RADAR_SWEEP_STATUS, {
    skip: !enabled,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  })

  const applyStatus = useCallback((status: RadarSweepStatus | null | undefined) => {
    // No answer (an error, a skipped query) changes nothing.
    if (!status) return
    const runStart = time(status.lastRunAt)
    const running =
      status.discoveryStatus === 'RUNNING' &&
      runStart !== null &&
      Date.now() - runStart < STALE_MS &&
      runStart > finishedRun.current
    setStartedAt(current => {
      if (running) return runStart
      // An idle status about an older run than the one showing is out of date.
      if (current !== null && (runStart === null || runStart < current)) return current
      return null
    })
    if (running) setLocalMark(null)
    // A settled run newer than the one marked here: the mark was for it.
    else if (runStart !== null) setLocalMark(mark => (mark !== null && runStart >= mark - LOCAL_CHECK_MS ? null : mark))
  }, [])

  useEffect(() => {
    applyStatus(data?.getAutoschedulerPostingStatus)
  }, [data, applyStatus])

  const resync = useCallback(() => {
    if (!enabled) return
    void refetch()
      .then(result => applyStatus(result.data?.getAutoschedulerPostingStatus))
      .catch(() => {})
  }, [enabled, refetch, applyStatus])

  // Signed out, or a guest: no radar here.
  useEffect(() => {
    if (!enabled) {
      setStartedAt(null)
      setLocalMark(null)
      finishedRun.current = 0
    }
  }, [enabled])

  useSubscription(USER_EVENTS_SUBSCRIPTION, {
    skip: !enabled,
    onData: ({ data: event }) => {
      const userEvent = event?.data?.userEvents
      if (userEvent?.type !== 'RADAR_SWEEP_STARTED' && userEvent?.type !== 'RADAR_SWEEP_FINISHED') return
      let runStart: number | null = null
      try {
        runStart = time(JSON.parse(userEvent.metadata || '{}').runStartedAt)
      } catch {
        // No run start in the event: fall back to now.
      }
      if (userEvent.type === 'RADAR_SWEEP_STARTED') {
        const at = runStart ?? Date.now()
        if (at <= finishedRun.current) return
        setStartedAt(at)
      } else {
        if (runStart !== null) finishedRun.current = Math.max(finishedRun.current, runStart)
        setStartedAt(null)
      }
      setLocalMark(null)
    },
  })

  // A dropped socket misses events: ask the server again once it is back.
  useEffect(() => {
    if (!enabled) return
    window.addEventListener('wsReconnected', resync)
    return () => window.removeEventListener('wsReconnected', resync)
  }, [enabled, resync])

  // A sweep marked here is checked with the server (the account is marked
  // running as the sweep starts, event or no event), and fades after the
  // grace period if the server never had it.
  useEffect(() => {
    if (localMark === null) return
    const check = window.setTimeout(resync, LOCAL_CHECK_MS)
    const fade = window.setTimeout(() => setLocalMark(null), LOCAL_GRACE_MS)
    return () => {
      window.clearTimeout(check)
      window.clearTimeout(fade)
    }
  }, [localMark, resync])

  // Nothing waits forever on a finish event that never came.
  useEffect(() => {
    if (startedAt === null) return
    const timer = window.setInterval(resync, CHECK_WHILE_SWEEPING_MS)
    return () => window.clearInterval(timer)
  }, [startedAt, resync])

  const noteSweepStarted = useCallback(() => {
    if (enabled) setLocalMark(Date.now())
  }, [enabled])
  const noteSweepSettled = useCallback(() => resync(), [resync])
  const noteStatus = useCallback((status: RadarSweepStatus | null | undefined) => {
    if (enabled) applyStatus(status)
  }, [enabled, applyStatus])

  const value = useMemo<RadarActivityValue>(() => ({
    sweeping: startedAt !== null || localMark !== null,
    noteSweepStarted,
    noteSweepSettled,
    noteStatus,
  }), [startedAt, localMark, noteSweepStarted, noteSweepSettled, noteStatus])

  return <RadarActivityContext.Provider value={value}>{children}</RadarActivityContext.Provider>
}

const outside: RadarActivityValue = { sweeping: false, noteSweepStarted: () => {}, noteSweepSettled: () => {}, noteStatus: () => {} }

/** Outside the provider (a test, a page rendered alone) nothing is sweeping. */
export function useRadarSweep(): RadarActivityValue {
  return useContext(RadarActivityContext) ?? outside
}
